import React, { useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaEdit, FaList } from 'react-icons/fa';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';
import { fetchCategories, clearError } from '../../../redux/Slices/AdminCategoriesSlice';

const AdminCategories = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const { categories, loading, error } = useSelector((state) => state.adminCategories);

    useEffect(() => {
        setTitle('إدارة الفئات');
        dispatch(fetchCategories());
    }, [setTitle, dispatch]);

    useEffect(() => {
        if (error) {
            showErrorToast(error);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    const handleEdit = (categoryId) => {
        navigate(`/admin-categories/${categoryId}/edit`);
    };

    const handleViewSubCategories = (categoryId) => {
        navigate(`/admin-categories/${categoryId}/sub-categories`);
    };

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">إدارة الفئات</h4>
                </div>

                {/* Categories Table */}
                <div className="card shadow-lg border-0">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">جاري تحميل الفئات...</p>
                            </div>
                        ) : !Array.isArray(categories) || categories.length === 0 ? (
                            <div className="text-center py-5">
                                <p className="text-muted">لا توجد فئات</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-modern">
                                    <thead>
                                        <tr>
                                            <th className="text-center">كود الفئة</th>
                                            <th className="text-center">اللوجو</th>
                                            <th className="text-center">صورة الويب</th>
                                            <th className="text-center">الاسم بالعربية</th>
                                            <th className="text-center">الاسم بالإنجليزية</th>
                                            <th className="text-center">الإجراءات</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {categories.map((category, index) => (
                                            <tr key={category.id} className="table-row-modern">
                                                <td className="text-center">
                                                    <div className="order-number-badge">
                                                        {category.code
                                                            || category.id
                                                            || category.id
                                                            || category.id
                                                            || '-'}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="category-logo-container">
                                                        {category.logo ? (
                                                            <img
                                                                src={category.logo}
                                                                alt={category.name?.ar}
                                                                className="category-logo"
                                                            />
                                                        ) : (
                                                            <div className="default-placeholder">
                                                                <FaList />
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="category-logo-container">
                                                        {category.web_img || category.web_image ? (
                                                            <img
                                                                src={category.web_img || category.web_image}
                                                                alt={`${category.name?.ar || 'صورة الويب'}`}
                                                                className="category-logo"
                                                            />
                                                        ) : (
                                                            <div className="default-placeholder">
                                                                <FaList />
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="category-name">
                                                        {category.name?.ar || 'غير محدد'}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="category-namx-en">
                                                        {category.name?.en || 'غير محدد'}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="action-buttons">
                                                        <motion.button
                                                            whileHover={{ scale: 1.1, y: -2 }}
                                                            whileTap={{ scale: 0.95 }}
                                                            className="btn btn-edit"
                                                            onClick={() => handleEdit(category.id)}
                                                            title="تعديل">
                                                            <FaEdit />
                                                        </motion.button>
                                                        <motion.button
                                                            whileHover={{ scale: 1.1, y: -2 }}
                                                            whileTap={{ scale: 0.95 }}
                                                            className="btn btn-subcategories"
                                                            onClick={() => handleViewSubCategories(category.id)}
                                                            title="الفئات الفرعية">
                                                            <FaList />
                                                        </motion.button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminCategories;

// CSS للتصميم الحديث
const style = document.createElement('style');
style.textContent = `
  /* تصميم الجدول الحديث */
  .table-modern {
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border: none;
  }
  
  .table-modern thead {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
  }
  
  .table-modern thead th {
    border: none;
    padding: 1rem;
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  .table-modern tbody tr {
    border-bottom: 1px solid #f1f5f9;
    transition: all 0.3s ease;
  }
  
  .table-modern tbody tr:hover {
    background-color: #f8fafc;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  .table-modern tbody td {
    border: none;
    padding: 1rem;
    vertical-align: middle;
    text-align: center;
  }
  
  /* تصميم رقم التصنيف */
  .order-number-badge {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-weight: 600;
    font-size: 0.875rem;
    display: inline-block;
    min-width: 60px;
    text-align: center;
  }
  
  /* تصميم لوجو التصنيف */
  .category-logo-container {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 auto;
  }
  
  .category-logo {
    width: 60px;
    height: 60px;
    border-radius: 8px;
    object-fit: cover;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    margin: 0 auto;
  }
  
  /* أيقونات افتراضية للبيانات الفارغة */
  .default-placeholder {
    width: 60px;
    height: 60px;
    background: #f8fafc;
    border: 2px dashed #cbd5e1;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    font-size: 1.2rem;
    transition: all 0.3s ease;
    margin: 0 auto;
  }
  
  .default-placeholder:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
    color: #64748b;
  }
  
  /* تصميم اسم التصنيف */
  .category-name, .category-namx-en {
    font-weight: 500;
    color: #374151;
    max-width: 150px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0 auto;
    text-align: center;
  }
  
  .category-namx-en {
    color: #64748b;
    font-size: 0.875rem;
  }
  
  /* تصميم أزرار الإجراءات */
  .action-buttons {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
    flex-direction: row;
    flex-wrap: nowrap;
  }
  
  .btn-edit, .btn-subcategories {
    border: none;
    color: white !important;
    padding: 0.25rem;
    border-radius: 8px;
    font-size: 1.2rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    width: 36px;
    height: 36px;
  }
  
  .btn-edit {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
  }
  
  .btn-subcategories {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
  }
  
  .btn-edit:hover, .btn-subcategories:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  .btn-edit * {
    color: white !important;
  }
`;
document.head.appendChild(style);
